// screen: Sites → visual page editor (Sites spec 5.1 "Visual editor", 5.2 editing rules; milestone W3)
// Three panels: block library + outline (left), the REAL renderer in an iframe with the editor bridge (centre, §17.2:
// never an editor-only rendering), properties of the selected section or of the page (right). Drafts autosave every
// 2 s with If-Match: draft_rev; a stale write (409) offers to reload the other editor's changes. A soft lock shows who
// is editing; others can take over after confirming. Publishing (Publisher) runs site_check and shows the diff
// against the live version; a version can be scheduled with a go-live and an optional go-off (takeover).
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { ArrowDown, ArrowLeft, ArrowUp, Copy, Eye, EyeOff, GripVertical, History, Link2, Monitor, Plus, Redo2, Smartphone, Tablet, Trash2, Tv, Undo2 } from 'lucide-react';
import type { ApiError, BlockInfo, PageVersion, Scope, SiteCheck, SitePageRow, SiteRow } from '@viewstream/api-client';
import { registry } from '@viewstream/blocks';
import { Badge, Button, Checkbox, Dialog, DialogContent, EmptyState, ErrorState, Input, Label, Sheet, SheetContent, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { useAuth } from '~/app/auth';
import { checkPage, lockPage, lz, previewLink, resolveSource, saveDraft, unlockPage, useBlocks, usePage, usePageActions, usePages, useSeries, useSites, useVersionActions, useVersions } from '~/app/sites';
import { cleanProps, cleanVisibility, diff, duplicate, emptyHistory, insert, isHidden, move, moveBy, newSection, normalise, push, remove, toggleHidden, tvStatus, update, type DiffEntry, type History as Hist, type LayoutDoc, type Section, type Visibility } from '~/app/siteBuilder/layout';
import { SchemaForm } from '~/app/siteBuilder/SchemaForm';
import { SiteImageContext } from '~/app/SiteImageField';
import { SourcePicker } from '~/app/siteBuilder/SourcePicker';
import { TvAppCanvas, tvAppSrc } from '~/app/siteBuilder/TvAppCanvas';
import { useEntities } from '~/app/entities';

export const Route = createFileRoute('/_layout/t/$slug/sites/$siteId/pages/$pageId')({ component: Screen });

const DEVICES = [{ w: 390, icon: Smartphone, key: 'mobile' }, { w: 820, icon: Tablet, key: 'tablet' }, { w: 1440, icon: Monitor, key: 'desktop' }, { w: 1920, icon: Tv, key: 'tv' }] as const;
type SaveState = 'saved' | 'dirty' | 'saving' | 'conflict' | 'error';
const TZ = 'Asia/Jerusalem';
const fmt = (iso?: string | null, locale = 'he') => (iso ? new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-GB', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short', hourCycle: 'h23' }).format(new Date(iso)) : '—');

function Screen() {
  const { t } = useLingui();
  const { siteId, pageId } = Route.useParams();
  const { tenant, scopes } = useTenant();
  const sites = useSites(tenant.slug);
  const page = usePage(tenant.slug, pageId);
  const blocks = useBlocks(tenant.slug, siteId);
  const site = sites.data?.find((s) => s.id === siteId);
  React.useEffect(() => { document.title = appTitle(t`Page editor`); }, [t]);
  if (sites.isLoading || page.isLoading || blocks.isLoading) return <Skeleton className="h-96" />;
  const err = (sites.error ?? page.error ?? blocks.error) as ApiError | null;
  if (err) return <ErrorState error={err} onRetry={() => { void page.refetch(); void sites.refetch(); void blocks.refetch(); }} labels={{ title: t`Could not load the page`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (!site || !page.data) return <EmptyState title={t`Page not found`} />;
  const canAdmin = can(scopes, 'sites:admin' as Scope);
  const canWrite = canAdmin || can(scopes, 'sites:write' as Scope);
  const canPublish = canAdmin || can(scopes, 'sites:publish' as Scope);
  return <Editor key={pageId} site={site} initial={page.data} blocks={blocks.data ?? []} canWrite={canWrite} canPublish={canPublish} canAdmin={canAdmin} />;
}

function Editor({ site, initial, blocks, canWrite, canPublish, canAdmin }: { site: SiteRow; initial: SitePageRow; blocks: BlockInfo[]; canWrite: boolean; canPublish: boolean; canAdmin: boolean }) {
  const { t, i18n } = useLingui();
  const blockName = useBlockName();
  const { tenant } = useTenant();
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const pages = usePages(tenant.slug, site.id);
  const status = pages.data?.find((p) => p.id === initial.id)?.status;
  const [doc, setDoc] = React.useState<LayoutDoc>(() => normalise(initial.draft));
  const [meta, setMeta] = React.useState({ title: initial.title as unknown, path: initial.path ?? '', seo: (initial.seo ?? {}) as Record<string, unknown> });
  const [rev, setRev] = React.useState(initial.draft_rev);
  const [save, setSave] = React.useState<SaveState>('saved');
  const [hist, setHist] = React.useState<Hist>(emptyHistory);
  const [sel, setSel] = React.useState<string | null>(null);
  const [lock, setLock] = React.useState<{ mine: boolean; by?: string | null } | null>(null);
  const [device, setDevice] = React.useState<number>(1440);
  const [tvApp, setTvApp] = React.useState(false); // phase 3: the real TV app instead of the site renderer
  const [lang, setLang] = React.useState<'he' | 'en'>('he');
  const [token, setToken] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const scrollY = React.useRef(0);
  const frame = React.useRef<HTMLIFrameElement>(null);
  const readOnly = !canWrite || (lock !== null && !lock.mine);
  const imageCtx = React.useMemo(() => ({ siteId: site.id, canWrite: !readOnly }), [site.id, readOnly]); // sites-images
  const host = site.hostnames[0];
  const origin = host ? `https://${host}` : '';

  // ---- lock (soft, with presence; heartbeat keeps it) ----
  const takeLock = React.useCallback(async (force = false) => {
    if (!canWrite) return;
    try { const r = await lockPage(initial.id, force); setLock({ mine: true, by: r.locked_by }); }
    catch (e) {
      const err = e as ApiError & { problem?: { locked_by?: string } };
      if (err.status === 423) setLock({ mine: false, by: (err.problem as unknown as { locked_by?: string } | null)?.locked_by ?? null });
    }
  }, [canWrite, initial.id]);
  const lockRef = React.useRef(lock);
  lockRef.current = lock;
  React.useEffect(() => {
    void takeLock();
    const h = setInterval(() => { if (lockRef.current?.mine) void lockPage(initial.id).catch(() => setLock({ mine: false })); }, 60_000);
    // closing the tab or leaving the app releases the lock too (a keepalive request survives the unload)
    const bye = () => {
      if (!lockRef.current?.mine) return;
      void fetch(`${import.meta.env['VITE_API_BASE'] ?? ''}/v1/pages/${initial.id}/lock`, { method: 'DELETE', keepalive: true, credentials: 'include', headers: { 'X-VS-Request': '1' } }).catch(() => undefined);
    };
    window.addEventListener('pagehide', bye);
    return () => { clearInterval(h); window.removeEventListener('pagehide', bye); if (lockRef.current?.mine) void unlockPage(initial.id).catch(() => undefined); };
  }, [takeLock, initial.id]);

  // ---- preview token for the canvas (24 h, re-issued on reload of the editor) ----
  React.useEffect(() => { if (canWrite) void previewLink(initial.id).then((r) => setToken(r.token)).catch(() => setToken(null)); }, [canWrite, initial.id]);

  // ---- editing with undo/redo ----
  const docRef = React.useRef(doc);
  docRef.current = doc;
  const histRef = React.useRef(hist);
  histRef.current = hist;
  const edit = React.useCallback((fn: (d: LayoutDoc) => LayoutDoc) => {
    if (readOnly) return;
    const d = docRef.current;
    const n = fn(d);
    if (n === d) return;
    docRef.current = n;
    setHist((histRef.current = push(histRef.current, d)));
    setDoc(n);
    setSave('dirty');
  }, [readOnly]);
  const undo = () => {
    const h = histRef.current; const prev = h.past[h.past.length - 1];
    if (!prev || readOnly) return;
    setHist((histRef.current = { past: h.past.slice(0, -1), future: [docRef.current, ...h.future] }));
    setDoc((docRef.current = prev)); setSave('dirty');
  };
  const redo = () => {
    const h = histRef.current; const next = h.future[0];
    if (!next || readOnly) return;
    setHist((histRef.current = { past: [...h.past, docRef.current], future: h.future.slice(1) }));
    setDoc((docRef.current = next)); setSave('dirty');
  };
  const setMetaDirty = (m: Partial<typeof meta>) => { if (readOnly) return; setMeta((x) => ({ ...x, ...m })); setSave('dirty'); };

  // ---- autosave every 2 s with If-Match (spec 5.2) ----
  const latest = React.useRef({ doc, meta, rev });
  latest.current = { doc, meta, rev };
  React.useEffect(() => {
    if (save !== 'dirty' || readOnly) return;
    const h = setTimeout(async () => {
      const { doc: d, meta: m, rev: r } = latest.current;
      setSave('saving');
      try {
        const body: Parameters<typeof saveDraft>[2] = { draft: d, title: m.title, seo: m.seo };
        if (initial.kind === 'page' && m.path && m.path !== initial.path) body.path = m.path;
        const res = await saveDraft(initial.id, r, body);
        setRev(res.draft_rev);
        setSave((s) => (s === 'saving' ? 'saved' : s));
        setReloadKey((k) => k + 1);
        void qc.invalidateQueries({ queryKey: ['t', tenant.slug, 'sites', site.id, 'pages'] });
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 409) setSave('conflict');
        else { setSave('error'); toast.push({ title: t`Could not save the draft`, description: err.problem?.detail ?? (err.problem as unknown as { errors?: Array<{ field: string; detail?: string; message?: string }> } | null)?.errors?.map((x) => `${x.field}: ${x.detail ?? x.message}`).join('; '), tone: 'danger' }); }
      }
    }, 2000);
    return () => clearTimeout(h);
  }, [save, readOnly, doc, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadFromServer = async () => {
    const { api } = await import('~/app/api');
    const fresh = (await api.GET('/v1/pages/{id}', { params: { path: { id: initial.id } } })).data as SitePageRow;
    setDoc(normalise(fresh.draft)); setMeta({ title: fresh.title, path: fresh.path ?? '', seo: (fresh.seo ?? {}) as Record<string, unknown> });
    setRev(fresh.draft_rev); setHist(emptyHistory()); setSave('saved'); setReloadKey((k) => k + 1);
  };
  const overwrite = async () => {
    const { api } = await import('~/app/api');
    const fresh = (await api.GET('/v1/pages/{id}', { params: { path: { id: initial.id } } })).data as SitePageRow;
    setRev(fresh.draft_rev); setSave('dirty');
  };

  // ---- canvas bridge ----
  const actions = React.useCallback((a: string, id: string, to?: number) => {
    if (a === 'up') edit((d) => moveBy(d, id, -1));
    else if (a === 'down') edit((d) => moveBy(d, id, 1));
    else if (a === 'move' && to != null) edit((d) => move(d, id, to));
    else if (a === 'duplicate') { let nid: string | null = null; edit((d) => { const r = duplicate(d, id); nid = r.id; return r.doc; }); if (nid) setSel(nid); }
    else if (a === 'hide') edit((d) => toggleHidden(d, id));
    else if (a === 'delete') { edit((d) => remove(d, id)); setSel(null); }
  }, [edit]);
  React.useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.origin !== origin || !e.data || e.data.source !== 'vs-bridge') return;
      const m = e.data as { type: string; id?: string; action?: string; to?: number; y?: number };
      if (m.type === 'ready') frame.current?.contentWindow?.postMessage({ source: 'vs-studio', type: 'select', id: selRef.current }, origin);
      else if (m.type === 'select' && m.id) setSel(m.id);
      else if (m.type === 'action' && m.id && m.action) actions(m.action, m.id, m.to);
      else if (m.type === 'scroll' && typeof m.y === 'number') scrollY.current = m.y;
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [origin, actions]);
  const selRef = React.useRef(sel);
  selRef.current = sel;
  React.useEffect(() => { if (origin) frame.current?.contentWindow?.postMessage({ source: 'vs-studio', type: 'select', id: sel }, origin); }, [sel, origin]);

  // ---- keyboard (spec 5.1): ↑/↓ move, D duplicate, H hide, Del remove, ⌘Z / ⌘⇧Z ----
  React.useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input, textarea, select, [contenteditable=true], [role=dialog]')) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (!sel || !el.closest('[data-vs-outline]')) return;
      if (e.key === 'ArrowUp' && e.altKey) { e.preventDefault(); actions('up', sel); }
      else if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); actions('down', sel); }
      else if (e.key.toLowerCase() === 'd' && !mod) { e.preventDefault(); actions('duplicate', sel); }
      else if (e.key.toLowerCase() === 'h' && !mod) { e.preventDefault(); actions('hide', sel); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); actions('delete', sel); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  });

  const entity = useSampleEntity(site.id, initial.template_for ?? null);
  const previewPath = initial.kind === 'page' ? (meta.path || '/') : '/';
  const entityKey = entity ? `${entity.type}:${entity.slug}` : '';
  // the canvas reloads only after a save, a device/language switch or a new example — never on selection or scroll
  const src = React.useMemo(() => (token && origin
    ? `${origin}/__preview${previewPath}?page=${initial.id}&token=${encodeURIComponent(token)}&edit=1${lang === 'en' ? '&lang=en' : ''}${entityKey ? `&entity=${encodeURIComponent(entityKey)}` : ''}${selRef.current ? `&sel=${selRef.current}` : ''}&sy=${Math.round(scrollY.current)}&r=${reloadKey}`
    : null), [token, origin, previewPath, initial.id, lang, entityKey, reloadKey]);
  const selected = doc.sections.find((s) => s.id === sel) ?? null;
  const blockInfo = (type: string) => blocks.find((b) => b.type === type);

  const saveLabel: Record<SaveState, string> = { saved: t`All changes saved`, dirty: t`Unsaved changes…`, saving: t`Saving…`, conflict: t`Someone else changed this page`, error: t`Not saved` };
  return (
    <SiteImageContext.Provider value={imageCtx}>
    <div className="flex h-[calc(100vh-7rem)] min-h-[560px] flex-col gap-3">
      <header className="flex flex-wrap items-center gap-2">
        <Link to="/t/$slug/sites" params={{ slug: tenant.slug }} className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"><ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />{t`Sites`}</Link>
        <h1 className="text-xl font-semibold">{lz(meta.title) || (initial.kind === 'template' ? initial.template_for : meta.path)}</h1>
        <span className="text-sm text-fg-muted" dir="ltr">{initial.kind === 'page' ? meta.path : `template: ${initial.template_for}`}</span>
        <StatusBadges status={status} />
        <span role="status" aria-live="polite" className={`text-sm ${save === 'conflict' || save === 'error' ? 'text-danger' : 'text-fg-muted'}`}>{saveLabel[save]}</span>
        <div className="ms-auto flex flex-wrap items-center gap-1">
          <Button size="sm" variant="ghost" onClick={undo} disabled={readOnly || !hist.past.length} aria-label={t`Undo`}><Undo2 className="size-4" aria-hidden /></Button>
          <Button size="sm" variant="ghost" onClick={redo} disabled={readOnly || !hist.future.length} aria-label={t`Redo`}><Redo2 className="size-4" aria-hidden /></Button>
          <Button size="sm" variant="secondary" onClick={() => setHistoryOpen(true)}><History className="size-4" aria-hidden />{t`History`}</Button>
          {canWrite ? <Button size="sm" variant="secondary" onClick={() => void previewLink(initial.id).then((r) => { void navigator.clipboard?.writeText(r.url); toast.push({ title: t`Preview link copied (valid 24 h)`, tone: 'ok' }); })}><Link2 className="size-4" aria-hidden />{t`Preview link`}</Button> : null}
          <Button size="sm" disabled={!canPublish || save !== 'saved'} title={!canPublish ? t`Only publishers can publish` : save !== 'saved' ? t`Wait for the draft to save` : undefined} onClick={() => setPublishOpen(true)}>{t`Publish…`}</Button>
        </div>
      </header>

      {lock && !lock.mine ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-warn/40 bg-warn/10 p-3 text-sm">
          <span>{t`Another user is editing this page. You can look, or take over — their unsaved changes may be lost.`}</span>
          <Button size="sm" variant="secondary" onClick={() => { if (window.confirm(t`Take over editing from the other user?`)) void takeLock(true); }}>{t`Take over`}</Button>
        </div>
      ) : null}
      {save === 'conflict' ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm">
          <span>{t`This page was changed elsewhere since you opened it. Reload their changes, or keep yours and overwrite?`}</span>
          <Button size="sm" onClick={() => void reloadFromServer()}>{t`Reload their changes`}</Button>
          <Button size="sm" variant="secondary" onClick={() => void overwrite()}>{t`Keep mine`}</Button>
        </div>
      ) : null}
      {!canWrite ? <p className="text-sm text-fg-muted">{t`You can view this page. Editing needs the Editor role.`}</p> : null}

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[260px_minmax(0,1fr)_340px]">
        {/* left: outline + block library */}
        <aside className="min-h-0 overflow-auto rounded-lg border border-border bg-surface p-2" aria-label={t`Page structure`}>
          <Tabs defaultValue="outline">
            <TabsList className="w-full"><TabsTrigger value="outline">{t`Outline`}</TabsTrigger><TabsTrigger value="add">{t`Add block`}</TabsTrigger></TabsList>
            <TabsContent value="outline" className="pt-2">
              <Outline doc={doc} sel={sel} onSelect={setSel} readOnly={readOnly} onAction={actions} />
            </TabsContent>
            <TabsContent value="add" className="pt-2">
              <ul className="space-y-1" aria-label={t`Block library`}>
                {blocks.filter((b) => canAdmin || !b.admin_only).filter((b) => registry[b.type]).map((b) => (
                  <li key={b.type}>
                    <button type="button" disabled={readOnly} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm hover:bg-surface-2 disabled:opacity-50"
                      onClick={() => { const s = newSection(b.type, b.sources ?? [], doc.sections.map((x) => x.id)); const at = sel ? doc.sections.findIndex((x) => x.id === sel) + 1 : doc.sections.length; edit((d) => insert(d, s, at)); setSel(s.id); }}>
                      <Plus className="size-4" aria-hidden /><span className="flex-1">{blockName(b.type)}</span>{b.island ? <Badge tone="neutral">JS</Badge> : null}
                    </button>
                  </li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>
        </aside>

        {/* centre: canvas = the real renderer */}
        <section className="flex min-h-0 flex-col rounded-lg border border-border bg-surface" aria-label={t`Preview`}>
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-2">
            <div role="group" aria-label={t`Device width`} className="inline-flex gap-1">
              {DEVICES.map(({ w, icon: Icon }) => <Button key={w} size="sm" variant={!tvApp && device === w ? 'primary' : 'ghost'} aria-pressed={!tvApp && device === w} onClick={() => { setTvApp(false); setDevice(w); }}><Icon className="size-4" aria-hidden /><span className="ltr-isolate">{w}</span></Button>)}
              {initial.kind === 'page' ? (
                <Button size="sm" variant={tvApp ? 'primary' : 'ghost'} aria-pressed={tvApp} onClick={() => setTvApp(true)} data-testid="device-tv-app"><Tv className="size-4" aria-hidden />{t`TV app`}</Button>
              ) : null}
            </div>
            {site.locales.includes('en') ? (
              <div role="group" aria-label={t`Language`} className="inline-flex gap-1">
                {(['he', 'en'] as const).map((l) => <Button key={l} size="sm" variant={lang === l ? 'primary' : 'ghost'} aria-pressed={lang === l} onClick={() => setLang(l)}>{l === 'he' ? 'עברית' : 'English'}</Button>)}
              </div>
            ) : null}
            {entity ? <span className="text-xs text-fg-muted">{t`Example`}: <span dir="auto">{entity.label}</span></span> : null}
          </div>
          {tvApp ? (
            <TvAppCanvas src={token ? tvAppSrc({ site: site.slug, tenant: tenant.slug, cdn: tenant.cdn_hostname, page: initial.id, token, reload: reloadKey }) : null} />
          ) : (
            <Canvas src={src} width={device} frameRef={frame} />
          )}
        </section>

        {/* right: properties */}
        <aside className="min-h-0 overflow-auto rounded-lg border border-border bg-surface p-3" aria-label={t`Properties`}>
          {selected ? (
            <SectionInspector key={selected.id} section={selected} info={blockInfo(selected.type)} site={site} readOnly={readOnly} entity={entity ?? undefined}
              onChange={(fn) => edit((d) => update(d, selected.id, fn))} onClose={() => setSel(null)} />
          ) : (
            <PageInspector kind={initial.kind} meta={meta} readOnly={readOnly} onChange={setMetaDirty} />
          )}
        </aside>
      </div>

      {publishOpen ? <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} site={site} page={initial} doc={doc} status={status} locale={i18n.locale} /> : null}
      <HistorySheet open={historyOpen} onOpenChange={setHistoryOpen} site={site} page={initial} canWrite={canWrite && !readOnly} canPublish={canPublish} locale={i18n.locale} me={me?.user?.id}
        onRestored={() => void reloadFromServer()} />
    </div>
    </SiteImageContext.Provider>
  );
}

function StatusBadges({ status }: { status?: SitePageRow['status'] }) {
  const { t, i18n } = useLingui();
  if (!status) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {status.live_version ? <Badge tone="ok">{t`Live`} · v{status.live_version}</Badge> : <Badge tone="neutral">{t`Not published`}</Badge>}
      {status.draft_changed && status.latest_version ? <Badge tone="warn">{t`Draft changes`}</Badge> : null}
      {status.scheduled_at ? <Badge tone="primary">{t`Scheduled`} {fmt(status.scheduled_at, i18n.locale)}</Badge> : null}
      {status.takeover_ends ? <Badge tone="live">{t`Takeover until`} {fmt(status.takeover_ends, i18n.locale)}</Badge> : null}
    </span>
  );
}

/** Block names for the library, outline and diff (a hook: the Lingui `t` macro must not be passed as a parameter). */
function useBlockName(): (type: string) => string {
  const { t } = useLingui();
  const n: Record<string, string> = {
    hero: t`Hero banner`, rail: t`Rail`, rail_top10: t`Top 10 rail`, grid: t`Grid`, live_strip: t`Live now strip`, epg: t`Programme guide`,
    catchup_days: t`Catch-up by day`, player: t`Player`, entity_header: t`Title and details`, people_rail: t`Presenters`, promo_tiles: t`Promo tiles`,
    podcast_list: t`Podcast episodes`, text: t`Text`, ad_slot: t`Ad slot`, html: t`Custom HTML`, countdown: t`Countdown`, search_box: t`Search box`,
    search_results: t`Search results`, continue_watching: t`Continue watching`, accessibility_statement: t`Accessibility statement`,
  };
  return (type: string) => n[type] ?? type;
}

// how the TV apps treat the section (plan-tv-section-in-sites phase 1): a quiet TV icon when shown, a badge otherwise
function TvMark({ s }: { s: Section }) {
  const { t } = useLingui();
  const st = tvStatus(s);
  if (st === 'shown') return <span title={t`Shown on TV`} className="shrink-0 text-fg-muted"><Tv className="size-3.5" aria-hidden /><span className="sr-only">{t`Shown on TV`}</span></span>;
  const label = st === 'screen' ? t`Own screen on TV` : st === 'hidden' ? t`Hidden on TV` : t`Not supported on TV`;
  const help = st === 'screen' ? t`The TV apps show this block as a screen of its own (guide, player or search), not on the page.` : st === 'hidden' ? t`TV is unticked in this section's Devices.` : t`The TV apps skip this block type.`;
  return <Badge tone={st === 'screen' ? 'neutral' : 'warn'} className="shrink-0" title={help}>{label}</Badge>;
}

function Outline({ doc, sel, onSelect, readOnly, onAction }: { doc: LayoutDoc; sel: string | null; onSelect: (id: string) => void; readOnly: boolean; onAction: (a: string, id: string, to?: number) => void }) {
  const { t } = useLingui();
  const blockName = useBlockName();
  const [drag, setDrag] = React.useState<string | null>(null);
  const [over, setOver] = React.useState<number | null>(null);
  if (!doc.sections.length) return <p className="p-2 text-sm text-fg-muted">{t`The page is empty. Add a block.`}</p>;
  return (
    <ol className="space-y-1" data-vs-outline aria-label={t`Sections (Alt+↑/↓ move, D duplicate, H hide, Delete remove)`}>
      {doc.sections.map((s, i) => {
        const title = lz((s.props as { title?: unknown } | undefined)?.title);
        return (
          <li key={s.id}
            draggable={!readOnly} onDragStart={(e) => { setDrag(s.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', s.id); }}
            onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(i); } }} onDragEnd={() => { setDrag(null); setOver(null); }}
            onDrop={(e) => { e.preventDefault(); if (drag) onAction('move', drag, i); setDrag(null); setOver(null); }}
            className={`group flex items-center gap-1 rounded border px-1 py-1 text-sm ${sel === s.id ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-surface-2'} ${over === i && drag && drag !== s.id ? 'border-t-2 border-t-warn' : ''} ${isHidden(s) ? 'opacity-60' : ''}`}>
            <GripVertical className="size-4 shrink-0 cursor-grab text-fg-muted" aria-hidden />
            <button type="button" className="min-w-0 flex-1 truncate text-start" aria-current={sel === s.id ? 'true' : undefined} onClick={() => onSelect(s.id)}>
              <span className="font-medium">{blockName(s.type)}</span>{title ? <span className="text-fg-muted"> · {title}</span> : null}
              {isHidden(s) ? <span className="sr-only"> ({t`hidden`})</span> : null}
            </button>
            <TvMark s={s} />
            <span className="hidden gap-0.5 group-hover:flex group-focus-within:flex">
              <Button size="icon" variant="ghost" className="size-7" disabled={readOnly || i === 0} aria-label={t`Move up`} onClick={() => onAction('up', s.id)}><ArrowUp className="size-3.5" aria-hidden /></Button>
              <Button size="icon" variant="ghost" className="size-7" disabled={readOnly || i === doc.sections.length - 1} aria-label={t`Move down`} onClick={() => onAction('down', s.id)}><ArrowDown className="size-3.5" aria-hidden /></Button>
              <Button size="icon" variant="ghost" className="size-7" disabled={readOnly} aria-label={t`Duplicate`} onClick={() => onAction('duplicate', s.id)}><Copy className="size-3.5" aria-hidden /></Button>
              <Button size="icon" variant="ghost" className="size-7" disabled={readOnly} aria-label={isHidden(s) ? t`Show` : t`Hide`} onClick={() => onAction('hide', s.id)}>{isHidden(s) ? <Eye className="size-3.5" aria-hidden /> : <EyeOff className="size-3.5" aria-hidden />}</Button>
              <Button size="icon" variant="ghost" className="size-7" disabled={readOnly} aria-label={t`Delete`} onClick={() => onAction('delete', s.id)}><Trash2 className="size-3.5" aria-hidden /></Button>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Canvas({ src, width, frameRef }: { src: string | null; width: number; frameRef: React.RefObject<HTMLIFrameElement | null> }) {
  const { t } = useLingui();
  const box = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState({ w: 800, h: 600 });
  React.useEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: e!.contentRect.width, h: e!.contentRect.height }));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  // the device width is rendered at full size and scaled down to fit (a 1920 TV page in an 800 px panel)
  const scale = Math.min(1, Math.max(0.2, (size.w - 16) / width));
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => { if (src) setLoading(true); }, [src]);
  return (
    <div ref={box} className="relative min-h-0 flex-1 overflow-hidden bg-bg p-2">
      {!src ? <p className="p-4 text-sm text-fg-muted">{t`The preview needs a site hostname and the Editor role.`}</p> : (
        // dir=ltr: in RTL a block wider than its box overflows to the left, which would push the scaled frame out of view
        <div dir="ltr" className="mx-auto overflow-hidden rounded border border-border bg-paper" style={{ width: width * scale, height: size.h - 16 }}>
          <iframe ref={frameRef} title={t`Page preview (editable)`} src={src} onLoad={() => setLoading(false)}
            style={{ width, height: (size.h - 16) / scale, transform: `scale(${scale})`, transformOrigin: '0 0', border: 0, display: 'block' }} />
        </div>
      )}
      {loading ? <span className="absolute end-3 top-3 rounded bg-surface px-2 py-0.5 text-xs text-fg-muted shadow">{t`Updating preview…`}</span> : null}
    </div>
  );
}

function SectionInspector({ section, info, site, readOnly, entity, onChange, onClose }: { section: Section; info?: BlockInfo; site: SiteRow; readOnly: boolean; entity?: { type: string; slug: string }; onChange: (fn: (s: Section) => Section) => void; onClose: () => void }) {
  const { t } = useLingui();
  const blockName = useBlockName();
  const def = registry[section.type];
  const props = (section.props ?? {}) as Record<string, unknown>;
  const kinds = (info?.sources ?? []).filter((k) => k !== '');
  const optional = (info?.sources ?? []).includes('');
  const shows = Number(props.limit ?? (section.type === 'rail_top10' ? 10 : 6));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">{blockName(section.type)}</h2>
        <span className="text-xs text-fg-muted" dir="ltr">#{section.id}</span>
        <Button size="sm" variant="ghost" className="ms-auto" onClick={onClose}>{t`Page settings`}</Button>
      </div>
      {def ? (
        <section className="space-y-2" aria-label={t`Block properties`}>
          <SchemaForm schema={def.props as never} value={props} idPrefix={`p-${section.id}`} disabled={readOnly}
            onChange={(v) => onChange((s) => ({ ...s, props: cleanProps(v) }))} />
        </section>
      ) : <p className="text-sm text-danger">{t`Unknown block type`}</p>}
      {kinds.length ? (
        <section className="space-y-2 border-t border-border pt-3" aria-label={t`Data source`}>
          <SourcePicker site={site.id} kinds={optional ? ['', ...kinds] : kinds} value={section.source ?? null} shows={shows} entity={entity} disabled={readOnly} idPrefix={`s-${section.id}`}
            onChange={(v) => onChange((s) => ({ ...s, source: v && v.kind ? v : undefined }))} />
        </section>
      ) : null}
      <VisibilityEditor value={section.visibility ?? null} readOnly={readOnly} id={section.id} onChange={(v) => onChange((s) => ({ ...s, visibility: v ?? undefined }))} />
    </div>
  );
}

const toLocal = (iso?: string | null) => { if (!iso || iso.startsWith('1970')) return ''; const d = new Date(iso); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`; };
const fromLocal = (v: string) => (v ? new Date(v).toISOString() : null);

function VisibilityEditor({ value, readOnly, id, onChange }: { value: Visibility | null; readOnly: boolean; id: string; onChange: (v: Visibility | null) => void }) {
  const { t } = useLingui();
  const v = value ?? {};
  const set = (p: Partial<Visibility>) => onChange(cleanVisibility({ ...v, ...p }));
  const devs = v.devices ?? [];
  const hidden = v.to?.startsWith('1970');
  return (
    <fieldset className="space-y-2 border-t border-border pt-3">
      <legend className="text-sm font-semibold">{t`Visibility`}</legend>
      {hidden ? <p className="text-sm text-warn">{t`This section is hidden. Use "Show" in the outline to bring it back.`}</p> : (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1"><Label htmlFor={`v-from-${id}`}>{t`From`}</Label><Input id={`v-from-${id}`} type="datetime-local" step={1} disabled={readOnly} value={toLocal(v.from)} onChange={(e) => set({ from: fromLocal(e.target.value) })} className="h-9" dir="ltr" /></div>
          <div className="space-y-1"><Label htmlFor={`v-to-${id}`}>{t`Until`}</Label><Input id={`v-to-${id}`} type="datetime-local" step={1} disabled={readOnly} value={toLocal(v.to)} onChange={(e) => set({ to: fromLocal(e.target.value) })} className="h-9" dir="ltr" /></div>
        </div>
      )}
      <div className="flex flex-wrap gap-3" role="group" aria-label={t`Devices (none = all)`}>
        {(['mobile', 'desktop', 'tv'] as const).map((d) => (
          <span key={d} className="flex items-center gap-1">
            <Checkbox id={`v-d-${id}-${d}`} disabled={readOnly} checked={devs.includes(d)} onCheckedChange={(c) => set({ devices: c === true ? [...devs, d] : devs.filter((x) => x !== d) })} />
            <Label htmlFor={`v-d-${id}-${d}`}>{d === 'mobile' ? t`Mobile` : d === 'desktop' ? t`Desktop` : t`TV`}</Label>
          </span>
        ))}
      </div>
      <p className="text-xs text-fg-muted">{t`Times are in your browser's time zone. A section outside its window disappears from the site by itself.`}</p>
    </fieldset>
  );
}

function PageInspector({ kind, meta, readOnly, onChange }: { kind: string; meta: { title: unknown; path: string; seo: Record<string, unknown> }; readOnly: boolean; onChange: (m: Partial<{ title: unknown; path: string; seo: Record<string, unknown> }>) => void }) {
  const { t } = useLingui();
  const title = (typeof meta.title === 'string' ? { he: meta.title } : (meta.title ?? {})) as { he?: string; en?: string };
  const seo = meta.seo as { title?: { he?: string; en?: string } | string; description?: { he?: string; en?: string } | string; noindex?: boolean };
  const lzObj = (x: unknown) => (typeof x === 'string' ? { he: x } : ((x ?? {}) as { he?: string; en?: string }));
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">{t`Page settings`}</h2>
      <p className="text-sm text-fg-muted">{t`Select a section in the preview or the outline to edit it.`}</p>
      {(['he', 'en'] as const).map((l) => (
        <div key={l} className="space-y-1"><Label htmlFor={`pg-title-${l}`}>{t`Title`} ({l})</Label>
          <Input id={`pg-title-${l}`} dir={l === 'he' ? 'rtl' : 'ltr'} disabled={readOnly} value={title[l] ?? ''} onChange={(e) => onChange({ title: { ...title, [l]: e.target.value } })} className="h-9" /></div>
      ))}
      {kind === 'page' ? (
        <div className="space-y-1"><Label htmlFor="pg-path">{t`Path`}</Label>
          <Input id="pg-path" dir="ltr" disabled={readOnly} value={meta.path} onChange={(e) => onChange({ path: e.target.value.startsWith('/') ? e.target.value : `/${e.target.value}` })} className="h-9 font-mono" /></div>
      ) : null}
      <fieldset className="space-y-2 border-t border-border pt-3">
        <legend className="text-sm font-semibold">{t`Search engines and sharing`}</legend>
        {(['he', 'en'] as const).map((l) => (
          <div key={l} className="space-y-1"><Label htmlFor={`seo-t-${l}`}>{t`SEO title`} ({l})</Label>
            <Input id={`seo-t-${l}`} dir={l === 'he' ? 'rtl' : 'ltr'} disabled={readOnly} value={lzObj(seo.title)[l] ?? ''} onChange={(e) => onChange({ seo: { ...meta.seo, title: { ...lzObj(seo.title), [l]: e.target.value } } })} className="h-9" /></div>
        ))}
        {(['he', 'en'] as const).map((l) => (
          <div key={l} className="space-y-1"><Label htmlFor={`seo-d-${l}`}>{t`Description`} ({l})</Label>
            <textarea id={`seo-d-${l}`} dir={l === 'he' ? 'rtl' : 'ltr'} rows={2} disabled={readOnly} value={lzObj(seo.description)[l] ?? ''} onChange={(e) => onChange({ seo: { ...meta.seo, description: { ...lzObj(seo.description), [l]: e.target.value } } })} className="w-full rounded-md border border-border bg-bg p-2 text-sm" /></div>
        ))}
        <div className="flex items-center gap-2"><Checkbox id="seo-noindex" disabled={readOnly} checked={!!seo.noindex} onCheckedChange={(c) => onChange({ seo: { ...meta.seo, noindex: c === true || undefined } })} /><Label htmlFor="seo-noindex">{t`Hide from search engines (noindex)`}</Label></div>
      </fieldset>
    </div>
  );
}

/** Templates render an entity; the canvas previews them with an example (the first show, the main channel, a recent VOD…). */
function useSampleEntity(site: string, templateFor: string | null): { type: string; slug: string; label: string } | null {
  const { tenant } = useTenant();
  const series = useSeries(tenant.slug);
  const companies = useEntities(tenant.slug, '', templateFor === 'company'); // v3 §3.1: the most-mentioned company
  const [ent, setEnt] = React.useState<{ type: string; slug: string; label: string } | null>(null);
  React.useEffect(() => {
    if (!templateFor) { setEnt(null); return; }
    if (templateFor === 'show' || templateFor === 'podcast') {
      const s = (series.data ?? []).find((x) => x.slug && (templateFor === 'podcast' ? x.kind === 'podcast' : x.kind !== 'podcast'));
      setEnt(s?.slug ? { type: 'series', slug: s.slug, label: s.title } : null);
      return;
    }
    if (templateFor === 'channel') { setEnt({ type: 'channel', slug: 'main', label: 'main' }); return; }
    if (templateFor === 'company') {
      const c = [...(companies.data ?? [])].filter((x) => x.status === 'confirmed').sort((a, b) => b.mentions - a.mentions)[0];
      setEnt(c ? { type: 'company', slug: c.slug, label: c.name_he } : null);
      return;
    }
    const src = templateFor === 'programme' ? { kind: 'catchup', days: 1 } : templateFor === 'clip' ? { kind: 'newest', types: ['clip'] } : templateFor === 'asset' ? { kind: 'newest', types: ['asset'] } : templateFor === 'person' ? { kind: 'people' } : null;
    if (!src) { setEnt(null); return; }
    let live = true;
    void resolveSource(site, src, 1).then((r) => { const it = r.items[0]; if (live) setEnt(it ? { type: it.type, slug: it.slug || it.id, label: lz(it.title) } : null); }).catch(() => undefined);
    return () => { live = false; };
  }, [templateFor, series.data, companies.data, site]);
  return ent;
}

// ---------------------------------------------------------------------------------------------------------------
// publish dialog (spec 5.1): diff against live, site_check, now or scheduled (go-live, optional go-off), note

function DiffList({ entries }: { entries: DiffEntry[] }) {
  const { t } = useLingui();
  const blockName = useBlockName();
  if (!entries.length) return <p className="text-sm text-fg-muted">{t`No changes against the live version.`}</p>;
  const label: Record<DiffEntry['kind'], string> = { added: t`Added`, removed: t`Removed`, moved: t`Moved`, changed: t`Changed` };
  const tone: Record<DiffEntry['kind'], 'ok' | 'danger' | 'primary' | 'warn'> = { added: 'ok', removed: 'danger', moved: 'primary', changed: 'warn' };
  return (
    <ul className="max-h-48 space-y-1 overflow-auto text-sm">
      {entries.map((e, i) => <li key={i} className="flex items-center gap-2"><Badge tone={tone[e.kind]}>{label[e.kind]}</Badge><span>{blockName(e.type)}</span><span className="text-fg-muted" dir="ltr">#{e.id}</span>{e.detail ? <span className="text-fg-muted" dir="ltr">({e.detail})</span> : null}</li>)}
    </ul>
  );
}

function CheckList({ check }: { check: SiteCheck | null }) {
  const { t } = useLingui();
  if (!check) return <Skeleton className="h-12" />;
  return (
    <div className="space-y-2 text-sm">
      {check.errors.length ? (
        <div role="alert"><p className="font-semibold text-danger">{t`Blocking accessibility errors (${check.errors.length})`}</p>
          <ul className="list-disc ps-5">{check.errors.map((e, i) => <li key={i}>{e.section ? <span dir="ltr">#{e.section}: </span> : null}{e.message}</li>)}</ul></div>
      ) : <p className="text-ok">{t`Accessibility check passed.`}</p>}
      {check.warnings.length ? (
        <div><p className="font-semibold text-warn">{t`Warnings (${check.warnings.length})`}</p>
          <ul className="list-disc ps-5">{check.warnings.map((e, i) => <li key={i}>{e.section ? <span dir="ltr">#{e.section}: </span> : null}{e.message}</li>)}</ul></div>
      ) : null}
    </div>
  );
}

function PublishDialog({ open, onOpenChange, site, page, doc, status }: { open: boolean; onOpenChange: (o: boolean) => void; site: SiteRow; page: SitePageRow; doc: LayoutDoc; status?: SitePageRow['status']; locale: string }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const versions = useVersions(tenant.slug, page.id);
  const act = usePageActions(tenant.slug, site.id);
  const [check, setCheck] = React.useState<SiteCheck | null>(null);
  const [mode, setMode] = React.useState<'now' | 'schedule'>('now');
  const [goLive, setGoLive] = React.useState('');
  const [goOff, setGoOff] = React.useState('');
  const [note, setNote] = React.useState('');
  React.useEffect(() => { void checkPage(page.id, doc).then(setCheck).catch(() => setCheck({ ok: false, errors: [{ code: 'check', message: t`site_check could not run` }], warnings: [] })); }, [page.id, doc, t]);
  const live = versions.data?.find((v) => v.version === status?.live_version) ?? null;
  const entries = diff(live ? normalise(live.layout) : null, doc);
  const invalidSchedule = mode === 'schedule' && (!goLive || (goOff !== '' && new Date(goOff) <= new Date(goLive)) || new Date(goLive) <= new Date());
  const submit = () => act.publish.mutate(
    { id: page.id, note: note || undefined, go_live_at: mode === 'schedule' ? fromLocal(goLive) ?? undefined : undefined, go_off_at: mode === 'schedule' && goOff ? fromLocal(goOff) ?? undefined : undefined },
    {
      onSuccess: (r) => { toast.push({ title: mode === 'schedule' ? t`Version ${r.version} scheduled` : t`Published version ${r.version}`, tone: 'ok' }); onOpenChange(false); },
      onError: (e) => { const p = (e as ApiError).problem as unknown as { detail?: string; check?: SiteCheck } | null; if (p?.check) setCheck(p.check); toast.push({ title: t`Could not publish`, description: p?.detail, tone: 'danger' }); },
    },
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t`Publish ${lz(page.title) || page.path || page.template_for}`} className="max-w-2xl">
        <div className="space-y-4">
          <section aria-label={t`Changes`}><h3 className="mb-1 text-sm font-semibold">{live ? t`Changes against the live version (v${live.version})` : t`First version of this page`}</h3>{versions.isLoading ? <Skeleton className="h-10" /> : <DiffList entries={entries} />}</section>
          <section aria-label={t`Checks`}><h3 className="mb-1 text-sm font-semibold">{t`Checks (site_check)`}</h3><CheckList check={check} /></section>
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold">{t`When`}</legend>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="radio" name="when" checked={mode === 'now'} onChange={() => setMode('now')} />{t`Publish now`}</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" name="when" checked={mode === 'schedule'} onChange={() => setMode('schedule')} />{t`Schedule`}</label>
            </div>
            {mode === 'schedule' ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1"><Label htmlFor="pub-live">{t`Goes live at`}</Label><Input id="pub-live" type="datetime-local" step={1} dir="ltr" value={goLive} onChange={(e) => setGoLive(e.target.value)} className="h-9" /></div>
                <div className="space-y-1"><Label htmlFor="pub-off">{t`Ends at (takeover, optional)`}</Label><Input id="pub-off" type="datetime-local" step={1} dir="ltr" value={goOff} onChange={(e) => setGoOff(e.target.value)} className="h-9" /></div>
                <p className="text-xs text-fg-muted sm:col-span-2">{t`A takeover goes live and, at its end, the page returns to the previous version by itself. Times are in your browser's time zone.`}</p>
              </div>
            ) : null}
          </fieldset>
          <div className="space-y-1"><Label htmlFor="pub-note">{t`Note (optional)`}</Label><Input id="pub-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-9" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>{t`Cancel`}</Button>
            <Button disabled={!check || !check.ok || invalidSchedule || act.publish.isPending} onClick={submit}>{mode === 'schedule' ? t`Schedule` : t`Publish`}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// history (spec 5.1): versions with actor, time, note and diff; restore as draft; re-publish; cancel a schedule

function HistorySheet({ open, onOpenChange, site, page, canWrite, canPublish, locale, onRestored }: { open: boolean; onOpenChange: (o: boolean) => void; site: SiteRow; page: SitePageRow; canWrite: boolean; canPublish: boolean; locale: string; me?: string; onRestored: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const versions = useVersions(tenant.slug, page.id);
  const act = useVersionActions(tenant.slug, site.id, page.id);
  const pub = usePageActions(tenant.slug, site.id);
  const [openV, setOpenV] = React.useState<number | null>(null);
  const now = Date.now();
  const list = versions.data ?? [];
  const state = (v: PageVersion) => {
    if (v.go_live_at && Date.parse(v.go_live_at) > now) return 'scheduled';
    if (v.go_off_at && Date.parse(v.go_off_at) <= now) return 'ended';
    return 'published';
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title={t`Version history`} className="w-[min(560px,100vw)]">
        {versions.isLoading ? <Skeleton className="h-40" /> : !list.length ? <p className="text-sm text-fg-muted">{t`Nothing published yet.`}</p> : (
          <ol className="space-y-2">
            {list.map((v, i) => {
              const prev = list[i + 1];
              const d = diff(prev ? normalise(prev.layout) : null, normalise(v.layout));
              const st = state(v);
              return (
                <li key={v.id} className="rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">v{v.version}</span>
                    <Badge tone={st === 'scheduled' ? 'primary' : st === 'ended' ? 'neutral' : 'ok'}>{st === 'scheduled' ? t`Scheduled` : st === 'ended' ? t`Ended` : t`Published`}</Badge>
                    <span className="text-sm text-fg-muted">{fmt(v.published_at, locale)}</span>
                    {v.go_live_at ? <span className="text-sm">{t`from`} {fmt(v.go_live_at, locale)}</span> : null}
                    {v.go_off_at ? <span className="text-sm">{t`until`} {fmt(v.go_off_at, locale)}</span> : null}
                  </div>
                  {v.note ? <p className="mt-1 text-sm">{v.note}</p> : null}
                  <button type="button" className="mt-1 text-sm text-primary underline-offset-4 hover:underline" aria-expanded={openV === v.version} onClick={() => setOpenV(openV === v.version ? null : v.version)}>{t`${d.length} changes`}</button>
                  {openV === v.version ? <div className="mt-2"><DiffList entries={d} /></div> : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canWrite && site.hostnames[0] ? <Button size="sm" variant="ghost" onClick={() => void previewLink(page.id).then((r) => { const u = new URL(r.url); u.searchParams.set('version', String(v.version)); window.open(u.toString(), '_blank', 'noopener'); })}>{t`Preview this version`}</Button> : null}
                    {canWrite ? <Button size="sm" variant="secondary" onClick={() => { if (window.confirm(t`Replace the current draft with version ${v.version}?`)) act.restore.mutate(v.version, { onSuccess: () => { toast.push({ title: t`Version ${v.version} restored as the draft`, tone: 'ok' }); onRestored(); } }); }}>{t`Restore as draft`}</Button> : null}
                    {canPublish && st !== 'scheduled' ? <Button size="sm" variant="secondary" onClick={() => { if (window.confirm(t`Publish version ${v.version} again now?`)) act.restore.mutate(v.version, { onSuccess: () => pub.publish.mutate({ id: page.id, note: `re-publish v${v.version}` }, { onSuccess: (r) => { toast.push({ title: t`Published version ${r.version}`, tone: 'ok' }); onRestored(); }, onError: (e) => toast.push({ title: t`Could not publish`, description: (e as ApiError).problem?.detail, tone: 'danger' }) }) }); }}>{t`Re-publish`}</Button> : null}
                    {canPublish && st === 'scheduled' ? <Button size="sm" variant="ghost" onClick={() => act.cancel.mutate(v.version, { onSuccess: () => toast.push({ title: t`Schedule cancelled`, tone: 'ok' }), onError: (e) => toast.push({ title: t`Could not cancel`, description: (e as ApiError).problem?.detail, tone: 'danger' }) })}>{t`Cancel schedule`}</Button> : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </SheetContent>
    </Sheet>
  );
}
